import * as React from "react";
import { Checkbox, TableCell, TableRow } from "@mui/material";
import {
  BaseTableCell,
  KeyValue,
  Query,
  SimpleTableRow,
} from "../../interfaces";
import ModalDialog from "../ModalDialog";
import BranchOfficeDeposits from "../../containers/BranchOfficeDeposits";
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";

interface Props {
  appState?: Query;
  tableData: SimpleTableRow[];
  totalsRow?: KeyValue;
  tableCheckbox?: KeyValue;
  app?: string;
  nonInteractive?: boolean;
}

export default function SimpleTableBody({
  appState,
  tableData,
  totalsRow,
  tableCheckbox,
  app,
  nonInteractive,
}: Props) {
  const [open, setOpen] = React.useState("");
  const [name, setName] = React.useState("");
  const [maxYearWithData, setMaxYearWithData] = React.useState("");

  const showModal = (cert: string, name: string) => {
    if (cert && name && appState) {
      setOpen(cert);
      setName(name);
    }
  };
  React.useEffect(() => {
    let isMounted = true;
    const cert = open;
    if (cert && appState && appState.filters) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?filters=CERT:"${cert}"&limit=350&fields=YEAR&get_unique=YEAR`,
        )
          .then((response:any) => {
            if (isMounted) {
              const years = response.data.map((item: KeyValue) => item.YEAR);
              if (years.includes(Number(appState.filters.lastYear)) || !years.length){
                setMaxYearWithData("");
              } else {
                const maxYear = Math.max(...years);
                setMaxYearWithData(maxYear.toString());
              }
            }
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => (isMounted = false));
      })();
    }
  },[appState, open]);

  const branchOfficeModal = appState ? (
    <ModalDialog
      title={name}
      display={open !== ""}
      setDisplay={() => setOpen("")}
      maxWidth="lg"
    >
      <BranchOfficeDeposits
        providedAppState={{
          ...appState,
          filters: {
            ...appState.filters,
            cert: open,
            lastYear: maxYearWithData !== "" ? maxYearWithData : appState.filters.lastYear,
          },
        }}
      />
    </ModalDialog>
  ) : (
    ""
  );
  return (
    <>
      {tableData.map((tableCell: SimpleTableRow, index: number) => {
        return (
          <TableRow
            id={`simple-table-row-${index}`}
            key={`${index}`}
            tabIndex={0}
            onKeyPress={tableCell.rowOnClick}
            onClick={tableCell.rowOnClick}
            sx={
              tableCell.rowOnClick
                ? {
                    "cursor": "pointer",
                    "&.MuiTableRow-root.MuiTableRow-hover:focus": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                    "&:hover": {
                      backgroundColor: "#D9E8F6",
                    },
                  }
                : {
                    "&:hover": {
                      backgroundColor: "#D9E8F6",
                    },
                  }
            }
          >
            {Object.keys(tableCell).map((key: string, cellIndex: number) => {
              const value = (tableCell[key] as BaseTableCell).value;
              const cert = (tableCell.CERT as BaseTableCell)?.value?.toString();
              const name =
                (tableCell.NAMEFULL as BaseTableCell)?.value?.toString() ??
                (tableCell.NAME as BaseTableCell)?.value?.toString();
              const noValue = !value && app === "failures";
              const displayValue = noValue ? "0" : value;
              const isInstName = key === "NAMEFULL" || key === "NAMEHCR";
              return key !== "rowOnClick" ? (
                cellIndex === 0 && tableCheckbox && value ? (
                  <TableCell
                    padding="checkbox"
                    sx={{
                      "&.MuiTableCell-paddingCheckbox": {
                        width: "fit-content",
                        textAlign: "left",
                      },
                    }}
                    id={`${key}-${index}`}
                    key={`${key}`}
                    style={(tableCell[key] as BaseTableCell).styles}
                  >
                    <Checkbox
                      id={`table-row-check-box${index}`}
                      color="primary"
                      checked={!!tableCheckbox.allCheckboxes[value?.toString()]}
                      value={value?.toString()}
                      onChange={(e: any) => {
                        tableCheckbox.checkboxClick(e.target.value);
                      }}
                      aria-label="select all"
                    />

                    {value}
                  </TableCell>
                ) : (
                  <TableCell
                    sx={{ maxWidth: 200 }}
                    id={`${key}-${index}`}
                    key={`${key}`}
                    style={
                      isInstName
                        ? {
                            color: "blue",
                            textDecoration: "underline",
                            fontWeight: "bold",
                            textAlign: "left",
                            cursor: "pointer",
                          }
                        : (tableCell[key] as BaseTableCell).styles
                    }
                    onClick={() => showModal(cert, name)}
                  >
                    {displayValue}
                  </TableCell>
                )
              ) : undefined;
            })}
          </TableRow>
        );
      })}
      {totalsRow
        ? totalsRow.map((tableCell: SimpleTableRow, index: number) => {
            return (
              <TableRow
                id={`simple-table-row-${index}-total`}
                key={`${index}`}
                hover={!nonInteractive}
                tabIndex={0}
                sx={
                  app === "failures"
                    ? {
                        "backgroundColor": "#1A4480",
                        "& > th, & > td, & .MuiTableCell-root, & .MuiTypography-root":
                          {
                            color: "#ffffff !important",
                          },
                        "& > td:nth-of-type(-n+2), & > th:nth-of-type(-n+2)": {
                          backgroundColor: "#003256 !important",
                        },
                      }
                    : {}
                }
              >
                {Object.keys(tableCell).map(
                  (key: string, totalIndex: number) => {
                    const value = (tableCell[key] as BaseTableCell).value;
                    const noValue =
                      !value && app === "failures" && key !== "title";
                    const displayValue = noValue ? "0" : value;
                    return totalIndex === 0 ? (
                      <TableCell
                        sx={{ maxWidth: 200 }}
                        id={`${key}-${totalIndex}`}
                        key={`${key}`}
                        style={{ fontWeight: "bold" }}
                      >
                        {displayValue === "0" ? "TOTAL" : displayValue}
                      </TableCell>
                    ) : (
                      <TableCell
                        sx={{ maxWidth: 200 }}
                        id={`${key}-${totalIndex}`}
                        key={`${key}`}
                        style={{ fontWeight: "bold" }}
                      >
                        {displayValue}
                      </TableCell>
                    );
                  },
                )}
              </TableRow>
            );
          })
        : undefined}
      {branchOfficeModal}
    </>
  );
}
